import React, {
  useMemo,
  useRef, useState,
} from 'react';
import {
  Box, Typography
} from '@mui/material';
import {
  AutoSizer, Column, defaultTableRowRenderer, Table, TableHeaderProps, SortDirection, SortDirectionType
} from 'react-virtualized';
import moment from 'utils/moment';
import { GetApp } from '@mui/icons-material';
import {
  altitude, bearing, calculateDistance, coordinate, distance, speed
} from 'helpers/unitsOfMeasure';
import { useSelector } from 'react-redux';
import { Settings } from 'reducers/settings';
import useTranslation from 'hooks/useTranslation';
import { ClassNameMap } from '@mui/styles';
import clsx from 'clsx';
import tokml from 'tokml';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { useAssetReports } from 'repositories/reports/hooks';
import { labelToDisplayLabel } from 'helpers/events';
import { gatewayToTransport } from 'helpers/transport';
import MultiSelectFilter from './multiSelectFilter';
import useStyles from './analysisbox-styles';

interface AnalysisboxProps {
  selectedAsset: Asset,
  selectedItem: any;
  selectedLeg: any;
  timezone: any;
  now: any;
  centerMapOnPosition: any;
  setSelectedReport: any;
  selectedMapId: any;
  setHighlightedReportId: any;
  setFollow: any;
  follow: boolean;
  organisationId: string,
  drawerHeight: number,
  setMostRecentDeviceReport: (date: number) => void,
}

const compareTimes = (end: number, start: number): string => (new Date(Math.max(0, (end - start)) * 1000)).toISOString().slice(11, 19);

// const compareTimes = (end: number, start: number): string => (end - start).toString()

interface Units {
  coordinate: string,
  altitude: string,
  bearing: string,
  speed: string,
  distance: string
}

const mapToAnalysisBox = (r: Report, units: Units, timezone: string, prev?: Report): AnalysisBoxReport => ({
  id: r.id,
  timestamp: (r.received && timezone)
    ? moment.unix(r.received).tz(timezone).format('D MMM YYYY H:mm:ss z')
    : '--',
  coordinates: (r.latitude && r.longitude && units.coordinate)
    ? coordinate.fromLatLon(r.latitude, r.longitude, units.coordinate)
    : '--',
  altitude: (r.altitude && units.altitude)
    ? altitude.fromSI(r.altitude, units.altitude)
    : '--',
  bearing: (r.course && r.received && units.bearing)
    ? bearing.fromSI(r.course, r.received, r, units.bearing)
    : '--',
  speed: (r.speed && units.speed)
    ? speed.fromKmh(r.speed, units.speed)
    : '--',
  event: r.events[0]
    ? labelToDisplayLabel(r.events[0])
    : 'Standard',
  // text: (textRecipient && textMessage)
  //   ? `${textRecipient}: ${textMessage}`
  //   : '--',
  dop: r.dop
    || '--',
  latency: (r.transmitted && r.received)
    ? compareTimes(r.transmitted, r.received)
    : '--',
  elapsed: (prev?.received && r.received)
    ? compareTimes(r.received, prev?.received)
    : '--',
  distance: (prev?.latitude && prev?.longitude && r.latitude && r.longitude)
    ? calculateDistance(prev, r, units.distance)
    : '--',
  gateway: r.gateway
    ? gatewayToTransport(r.gateway)
    : '--',
  metadata: r.metadata
    ? JSON.stringify(r.metadata, null, 1).slice(1, -1).replace(/["|\n]|(\\n)/g, '')
    : '--',
  latitude: r.latitude,
  longitude: r.longitude,
  received: r.received,
  deviceId: r.deviceId,
  isValid: r.isValid
});

interface SortIndicatorProps {
  sortDirection?: SortDirectionType
}
const SortIndicator = ({ sortDirection }: SortIndicatorProps) => {
  const className = clsx('ReactVirtualized__Table__sortableHeaderIcon', {
    'ReactVirtualized__Table__sortableHeaderIcon--ASC': sortDirection === SortDirection.ASC,
    'ReactVirtualized__Table__sortableHeaderIcon--DESC': sortDirection === SortDirection.DESC
  });

  return (
    <svg
      className={className}
      width={18}
      height={18}
      viewBox="0 0 24 24"
      focusable={false}
      aria-hidden
    >
      <path d="M 20 12 l -1.41 -1.41 L 13 16.17 V 4 h -2 v 12.17 l -5.58 -5.59 L 4 12 l 8 8 l 8 -8 Z" />
    </svg>
  );
};

const HeaderRenderer = ({
  dataKey,
  label,
  sortBy,
  sortDirection
}: TableHeaderProps) => {
  const showSortIndicator = sortBy === dataKey;
  const children = [
    (
      <span
        className="ReactVirtualized__Table__headerTruncatedText"
        key="label"
        title={typeof label === 'string' ? label : undefined}
      >
        {label}
      </span>
    )
  ];

  if (showSortIndicator) {
    children.push(<SortIndicator key="SortIndicator" sortDirection={sortDirection} />);
  }

  return children;
};

const Analysisbox = ({
  selectedAsset,
  selectedItem,
  selectedLeg,
  timezone,
  now,
  centerMapOnPosition,
  setSelectedReport,
  selectedMapId,
  setHighlightedReportId,
  setFollow,
  follow,
  organisationId,
  drawerHeight,
  setMostRecentDeviceReport,
}: AnalysisboxProps): JSX.Element => {
  const classes: ClassNameMap = useStyles();
  const t = useTranslation('analysisbox');
  const units = useSelector(({ settings }: { settings: Settings }) => settings.units);
  const {
    altitude: altitudeUnits,
    distance: distanceUnits,
    bearing: bearingUnits,
    speed: speedUnits,
    coordinate: coordinateUnits,
  } = units;
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [selectedFields, setSelectedFields] = useState(['timestamp', 'coordinates', 'altitude', 'bearing', 'speed', 'event', 'latency', 'elapsed', 'distance', 'gateway']);
  const [sort, setSort] = useState({ sortBy: 'timestamp', sortDirection: 'DESC' });
  const [exportSpeedDialOpen, setExportSpeedDialOpen] = useState(false);
  const tableRef = useRef(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const reportsForAsset = useAssetReports(selectedAsset.id);
  const selectedReports = useMemo(() => {
    if (selectedItem) {
      if (selectedLeg) {
        console.log(selectedLeg);
        return reportsForAsset
          .filter(r => r.received >= selectedLeg.start && (!selectedLeg.complete || r.received <= selectedLeg.end));
      }
      if (selectedItem) {
        return reportsForAsset;
      }
    }
    return [];
  }, [selectedItem, selectedLeg, reportsForAsset]);

  const analysisBoxReports = useMemo(() => selectedReports
    .reverse()
    .map((r, i, a) => mapToAnalysisBox(r, units, timezone, a[i - 1])), [selectedReports, units, timezone]);

  const dateSort = (a: AnalysisBoxReport, b: AnalysisBoxReport): number => moment(a.timestamp).tz(timezone).unix() - moment(b.timestamp).tz(timezone).unix();
  const alphabeticalSort = (a: any, b: any): number => a[sort.sortBy]?.localeCompare(b[sort.sortBy]);
  const numericalSort = (a: any, b: any): number => {
    const numericalA = typeof a[sort.sortBy] === 'number' ? a[sort.sortBy] : a[sort.sortBy]?.replace(/\D/g, '');
    const numericalB = typeof b[sort.sortBy] === 'number' ? b[sort.sortBy] : b[sort.sortBy]?.replace(/\D/g, '');
    return numericalA - numericalB;
  };

  const getSortMethod = (): (a: any, b: any) => number => {
    switch (sort.sortBy) {
      case 'timestamp': return dateSort;
      case 'altitude':
      case 'bearing':
      case 'speed':
      case 'dop':
      case 'distance':
        return numericalSort;
      default: return alphabeticalSort;
    }
  };

  const filteredReports = analysisBoxReports
    .filter(r => (selectedEvents.length ? selectedEvents.includes(r.event) : r))
    .sort(getSortMethod());
  const visibleReports = sort.sortDirection === 'DESC' ? filteredReports.reverse() : filteredReports;
  const visibleReportsWithUnits = visibleReports.map(r => ({
    ...r,
    altitude: r.altitude !== '--' ? altitude.withUnits(r.altitude, altitudeUnits, 0) : r.altitude,
    bearing: r.bearing !== '--' ? bearing.withUnits(r.bearing, bearingUnits, 0, r.received) : r.bearing,
    speed: r.speed !== '--' ? speed.withUnits(r.speed, speedUnits, 1) : r.speed,
    distance: r.distance !== '--' ? distance.withUnits(r.distance, distanceUnits, 2) : r.distance,
  }));

  const reportTitle = selectedItem ? selectedLeg ? `${selectedLeg.from} - ${selectedLeg.to || ''}` : selectedAsset?.name : '';
  const titleText = `${reportTitle} (${visibleReportsWithUnits.length} reports)`;
  const exportFilename = `TracPlus Export ${reportTitle} ${moment(now ?? new Date()).format('YYYY-MM-DD')}`;
  const uniqueEvents = analysisBoxReports.map(r => r.event).filter((e, i, self) => self.indexOf(e) === i);

  const fields = {
    id: { label: 'id', dataKey: 'id', width: () => 0 },
    timestamp: { label: t('timestamp'), dataKey: 'timestamp', width: () => 190 },
    coordinates: { label: t('coordinates'), dataKey: 'coordinates', width: () => 200 },
    altitude: { label: t('altitude'), dataKey: 'altitude', width: () => 90 },
    bearing: { label: t('bearing'), dataKey: 'bearing', width: () => 90 },
    speed: { label: t('speed'), dataKey: 'speed', width: () => 100 },
    event: { label: t('event'), dataKey: 'event', width: () => 100 },
    text: { label: t('text'), dataKey: 'text', width: () => 100 },
    dop: { label: t('dop'), dataKey: 'dop', width: () => 80 },
    latency: { label: t('latency'), dataKey: 'latency', width: () => 100 },
    elapsed: { label: t('elapsed'), dataKey: 'elapsed', width: () => 100 },
    distance: { label: t('distance'), dataKey: 'distance', width: () => 100 },
    gateway: { label: t('gateway'), dataKey: 'gateway', width: () => 100 },
    metadata: { label: t('metadata'), dataKey: 'metadata', width: () => 700 },
  };

  const getRowRenderer = (props: any): React.ReactNode => defaultTableRowRenderer({
    ...props,
    onRowClick: row => {
      // centering map on past reports for a followed asset causes bad behavior, so unfollow first
      if (follow) setFollow(selectedMapId, false);
      centerMapOnPosition(row.rowData);
      setHighlightedReportId(row.rowData.id);
      setSelectedRow(row.rowData);
    },
    onRowMouseOver: row => setSelectedReport(selectedMapId, visibleReports.find(r => r.id === row.rowData.id)),
  });

  const handleSort = ({ sortBy, sortDirection }: { sortBy: any, sortDirection: SortDirectionType}): void => setSort({ sortBy, sortDirection });
  const prepareCSVExport = (): string => {
    const rows = visibleReports
      .map(r => {
        const noDashesReport = {};
        Object.keys(r).forEach(k => {
          (noDashesReport as any)[k] = (r as any)[k] === '--' ? '' : (r as any)[k];
        });
        const [latitude, longitude] = r.coordinates === '--' ? ['', ''] : r.coordinates.split(', ');
        return { latitude, longitude, ...noDashesReport };
      })
      // exclude id, coordinates and position columns from the CSV
      .map(({
        id, coordinates, position, ...rest
      }: any) => rest);
    const unitsLookup = {
      timestamp: timezone, altitude: altitudeUnits, bearing: bearing.label(bearingUnits, visibleReports[0]?.received), speed: speedUnits, distance: distanceUnits
    };
    const CSVData = [
      Object.keys(rows[0]).map(value => ((unitsLookup as any)[value] ? `"${value} (${(unitsLookup as any)[value]})"` : `"${value}"`)),
      ...rows
        .map(r => Object.values(r).map(value => `"${value}"`))
    ].map(e => e.join(',')).join('\n');
    return `data:text/csv;charset=utf-8,${CSVData}`;
  };
  const downloadCsvFile = (): void => {
    const encodedUri = encodeURI(prepareCSVExport());
    const element = document.createElement('a');
    element.setAttribute('href', encodedUri);
    element.setAttribute('download', `${exportFilename}.csv`);
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };
  const prepareKMZExport = () => {
    const geoJSON = {
      type: 'FeatureCollection',
      name: exportFilename,
    };
    const features = visibleReportsWithUnits
      .filter(r => r.latitude && r.longitude)
      .map((r, i) => ({
        type: 'Feature',
        properties: {
          Asset: selectedAsset.name,
          Message: `message ${i + 1} of ${selectedReports.length}`,
          Event: r.event,
          Timestamp: r.timestamp,
          Position: `${r.longitude}, ${r.latitude}`,
          Altitude: r.altitude,
          Speed: r.speed,
          Bearing: r.bearing,
          Gateway: r.gateway,
          'Altitude Mode': 'absolute',
        },
        geometry: {
          type: 'Point',
          coordinates: [r.longitude, r.latitude]
        }
      }));
    return tokml({ ...geoJSON, features }, { documentName: exportFilename });
  };
  const downloadKmzFile = (): void => {
    const element = document.createElement('a');
    const blob = new Blob([prepareKMZExport()]); // , {type: 'text/plain'});
    element.href = URL.createObjectURL(blob);
    element.download = `${exportFilename}.kml`;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };
  const getRowClassName = (index: any): string => (selectedRow && visibleReportsWithUnits.findIndex(row => row.id === selectedRow?.id) === index.index ? classes.focusedRow : '');

  return (
    <Box className={classes.tableContainer}>
      <Typography className={classes.assetTitle} variant="h2">{titleText}</Typography>

      <MultiSelectFilter
        options={visibleReportsWithUnits.length ? Object.keys(visibleReportsWithUnits[0])?.filter(key => Object.keys(fields).includes(key)) : []}
        selectedOptions={selectedFields}
        setSelectedOptions={setSelectedFields}
        label={t('fieldFilter.selectColumns')}
        summaryText={t('fieldFilter.summaryText')}
      />
      <MultiSelectFilter
        options={uniqueEvents}
        selectedOptions={selectedEvents}
        setSelectedOptions={setSelectedEvents}
        label={t('eventFilter.filterEvents')}
        summaryText={t('eventFilter.summaryText')}
      />

      <Box className={classes.exampleWrapper}>
        <ClickAwayListener onClickAway={() => setExportSpeedDialOpen(false)}>
          <SpeedDial
            ariaLabel="SpeedDial example"
            className={classes.speedDial}
            icon={<GetApp />}
            onClick={() => setExportSpeedDialOpen(!exportSpeedDialOpen)}
            open={exportSpeedDialOpen}
            direction="down"
          >
            <SpeedDialAction
              key="csv-download"
              icon={<GetApp />}
              tooltipTitle={`${t('export')} CSV`}
              onClick={() => downloadCsvFile()}
              tooltipOpen
            />
            <SpeedDialAction
              key="kmz-download"
              icon={<GetApp />}
              tooltipTitle={`${t('export')} KML`}
              onClick={() => downloadKmzFile()}
              tooltipOpen
            />
          </SpeedDial>
        </ClickAwayListener>
      </Box>

      <div className={classes.autosizerWrapper} ref={tableRef}>
        <AutoSizer disableHeight>
          {({ width }) => (
            <Table
              className={classes.reportsTable}
              width={width}
              height={drawerHeight - 90}
              headerHeight={49}
              rowHeight={37}
              rowCount={visibleReportsWithUnits.length}
              rowGetter={({ index }) => visibleReportsWithUnits[index]}
              sort={handleSort}
              sortBy={sort.sortBy}
              sortDirection={sort.sortDirection as SortDirectionType}
              rowRenderer={getRowRenderer}
              rowClassName={index => getRowClassName(index)}
              // onRowClick={this.rowSelection}
              scrollToAlignment="auto"
              scrollToIndex={visibleReportsWithUnits.indexOf(selectedRow)}
              scrollToRow={visibleReportsWithUnits.indexOf(selectedRow)}
              // onRowsRendered={this.rowRendered}
            >
              {selectedFields.map(field => (
                <Column
                  key={(fields as any)[field].dataKey}
                  label={(fields as any)[field].label}
                  dataKey={(fields as any)[field].dataKey}
                  flexGrow={1}
                  width={(fields as any)[field].width(width)}
                  headerRenderer={HeaderRenderer}
                />
              ))}
            </Table>
          )}
        </AutoSizer>
      </div>
    </Box>
  );
};

export default Analysisbox;
